import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import React, { useEffect } from 'react';
import '../global.scss';
import { ErrorBoundary, ErrorFallback } from './ErrorBoundary';
import { ServerSideEventProvider } from './ServerSideEventContext';
import { EnvironmentPermissionProvider } from './auth/EnvironmentPermissionContext';
import { UserProvider } from './auth/UserContext';
import AppToast from './components/AppToast';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';
import { BreakpointProvider } from './components/layout/BreakpointProvider';
import { DeepLinkProvider } from './deepLink';
import { I18nProvider } from './i18n/I18n';
import { getL10nDef } from './i18n/localization';
import db from './indexedDB';
import { ListViewCreateSessionProvider } from './list/listViewCreateSession';
import { RouteType } from './nav/AppRoute';
import AppRouter from './nav/AppRouter';
import { SidePanelSystemProvider } from './sidePanels';
import { StylingProvider } from './themeContext/StylingProvider';

interface EnvConfig {
    VERSION?: string;
    NAMESPACE?: string;
    AUTHORIZE_ENDPOINT?: string;
    TOKEN_ENDPOINT?: string;
    CLIENT_ID?: string;
    CLIENT_SECRET?: string;
    MATERIAL_PRO_KEY?: string;
    GOOGLE_MAPS_LOCATION_API_KEY?: string;
    BASE_URI?: string;
    MONGO_CHART_BASE_URL?: string;
    GOOGLE_CLIENT_ID?: string;
    MICROSOFT_CLIENT_ID?: string;
    MICROSOFT_REDIRECT_URI?: string;
    GOOGLE_TIMEZONE_API_KEY?: string;
    // add any other properties you need
}

declare global {
    interface Window {
        DEBUG: boolean;
        _env_: EnvConfig;
    }
}

const l10nDef = getL10nDef();
const defaultBundle = require('./i18n/bundles/en-US.json').default;

type AppProps = {
    routes: RouteType[];
};

/**
 * @todo
 * Either move this component to its own file, or inline it within App since it's only used there.
 */
const ShowrunnrApp = ({ routes }) => {
    /**
     * @todo: Extract to the module level to run imperatively once on module load.
     * Review usage of indexedDB wrapper to ensure such a refactor is feasible.
     */
    useEffect(() => {
        // Initialize IndexDB
        db.open();
    }, []);

    return (
        <UserProvider>
            <DeepLinkProvider>
                <SidePanelSystemProvider>
                    <StylingProvider>
                        <CssBaseline />
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={l10nDef.dateFns}>
                            <I18nProvider langBundle={l10nDef.appBundle} defaultBundle={defaultBundle}>
                                <GoogleMapsProvider>
                                    <ServerSideEventProvider>
                                        <EnvironmentPermissionProvider>
                                            <ListViewCreateSessionProvider>
                                                <AppRouter routes={routes} />
                                            </ListViewCreateSessionProvider>
                                        </EnvironmentPermissionProvider>
                                    </ServerSideEventProvider>
                                </GoogleMapsProvider>
                                <AppToast />
                            </I18nProvider>
                        </LocalizationProvider>
                    </StylingProvider>
                </SidePanelSystemProvider>
            </DeepLinkProvider>
        </UserProvider>
    );
};

export default function App(props: AppProps) {
    const { routes } = props;

    return (
        // <StrictMode>
        <ErrorBoundary fallbackRender={ErrorFallback}>
            <BreakpointProvider>
                <ShowrunnrApp routes={routes} />
            </BreakpointProvider>
        </ErrorBoundary>
        // </StrictMode>
    );
}
